/**
 * ArticleHeader — meta line, title, lead, tags, and cover image for a single blog article
 */
import { Image } from "@shopify/hydrogen-react";
import { useTranslation } from "react-i18next";
import { formatArticleDate } from "./formatArticleDate";

interface ArticleHeaderProps {
  title: string;
  publishedAt: string;
  locale: string;
  readingMinutes: number;
  authorName?: string | null;
  excerpt?: string | null;
  tags?: Array<string>;
  imageUrl?: string | null;
  imageAlt?: string | null;
}

export function ArticleHeader({
  title,
  publishedAt,
  locale,
  readingMinutes,
  authorName,
  excerpt,
  tags,
  imageUrl,
  imageAlt,
}: Readonly<ArticleHeaderProps>) {
  const { t } = useTranslation();

  return (
    <header>
      <div className="mx-auto max-w-[42rem]">
        <p className="text-small text-text-4 tabular-nums">
          <time dateTime={publishedAt}>
            {formatArticleDate(publishedAt, locale, "long")}
          </time>
          <span aria-hidden="true"> · </span>
          {readingMinutes} {t("Blog.minuteRead")}
          {authorName && (
            <>
              <span aria-hidden="true"> · </span>
              {authorName}
            </>
          )}
        </p>

        <h1 className="mt-4 text-3xlarge leading-tight font-bold break-words hyphens-auto text-balance text-text-1 max-mobile:text-xlarge">
          {title}
        </h1>

        {excerpt && (
          <p className="mt-5 text-medium text-pretty text-text-3 max-mobile:text-base">
            {excerpt}
          </p>
        )}

        {tags && tags.length > 0 && (
          <ul className="mt-6 flex flex-wrap gap-2" aria-label={t("Blog.tags")}>
            {tags.map((tag) => (
              <li
                key={tag}
                className="rounded-full bg-background-7 px-3 py-1 text-tny font-medium text-text-3"
              >
                {tag}
              </li>
            ))}
          </ul>
        )}
      </div>

      {imageUrl && (
        <div className="mx-auto mt-10 aspect-16/9 max-w-4xl overflow-hidden rounded-2xl bg-background-5 outline-1 -outline-offset-1 outline-black/10">
          <Image
            src={imageUrl}
            alt={imageAlt ?? title}
            className="size-full object-cover"
            loading="eager"
            sizes="(min-width: 960px) 896px, 100vw"
          />
        </div>
      )}
    </header>
  );
}
